import { useCallback, useEffect, useState } from 'react'
import { Button } from '@heroui/react'
import { api } from '../lib/api'
import { explain } from '../lib/staffErrors'
import { formatMoment, type StaffKey } from '../lib/staffText'
import type { Feed, FeedCheck } from '../lib/types'
import { Icon } from '../ui/icons'
import { StatusChip } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import { connectionText, feedFileAddress, feedState, FEED_SCHEMA, FEED_SSLMODE, problemKey, type FeedState } from './feedLogic'
import { Masked, OneTimeSecret, TestResult } from './connectParts'
import { Confirm, useFlash } from './sectionKit'
import { Notice, PageHead, SettingBlock, SettingRow, SettingsCard, Values } from './settingsKit'
import '../styles/connect.css'

/**
 * Administration → Data feed: the read-only login a bank's BI tools connect with,
 * and the key of its file feed (docs/REPORTING-FEED.md). The organisation's
 * administrator manages their own; the installation owner chooses the organisation.
 *
 * A password or key is held only in this component's state, from the answer that
 * made it until Done; the server never sends it again. The DBA's script carries a
 * SCRAM verifier, not the password.
 */

type Action = 'enable' | 'disable' | 'password' | 'check' | 'key'

const STATE_KEY: Record<FeedState, StaffKey> = {
  off: 'feed.state.off',
  waitingForDba: 'feed.state.waitingForDba',
  on: 'feed.state.on',
  broken: 'feed.state.broken',
  notOurs: 'feed.state.notOurs',
}

const STATE_TONE: Record<FeedState, 'ok' | 'warn' | 'bad' | undefined> = {
  off: undefined,
  waitingForDba: 'warn',
  on: 'ok',
  broken: 'warn',
  notOurs: 'bad',
}

const canCopy = () => typeof navigator !== 'undefined' && Boolean(navigator.clipboard)

export function DataFeed({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const { flash, toast } = useFlash()
  const [feed, setFeed] = useState<Feed | null>(null)
  const [check, setCheck] = useState<FeedCheck | undefined>()
  const [secret, setSecret] = useState<{ kind: 'password' | 'key'; value: string } | null>(null)
  // The revoke script a Turn off may hand back, shown until the next action.
  const [offScript, setOffScript] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      setFeed((await api.reportingFeed(organisation)).feed)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    }
    // lang deliberately not a dependency: a language switch must not reload.
  }, [organisation])

  useEffect(() => {
    void load()
  }, [load])

  const copy = (text: string) => {
    if (!canCopy()) return
    void navigator.clipboard.writeText(text).then(() => flash(say('common.copied')), () => {})
  }

  const act = async (action: Action) => {
    setBusy(true)
    setError('')
    try {
      const answer = await api.feedAction(action, organisation)
      setFeed(answer.feed)
      setCheck(action === 'check' ? answer.check : undefined)
      setOffScript(action === 'disable' ? (answer.script ?? '') : '')
      if (answer.password) setSecret({ kind: 'password', value: answer.password })
      else if (answer.key) setSecret({ kind: 'key', value: answer.key })
      else if (action === 'disable') setSecret(null)
      if ((action === 'enable' && answer.feed.enabled) || answer.check?.turnedOn) flash(say('feed.turnedOn'))
      if (action === 'disable') flash(say(answer.loginUntouched ? 'feed.offUntouched' : 'feed.turnedOff'))
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  if (!feed) {
    return (
      <>
        <PageHead title={say('admin.section.dataFeed')} line={say('connect.line.dataFeed')} />
        {error && <Notice>{error}</Notice>}
      </>
    )
  }

  const state = feedState(feed, check)
  const live = feed.enabled || feed.awaitingDba
  const script = feed.script || offScript
  const facts: Array<[StaffKey, string]> = [
    ['feed.host', feed.host],
    ['feed.port', String(feed.port)],
    ['feed.database', feed.database],
    ['feed.login', feed.login],
    ['feed.schema', FEED_SCHEMA],
  ]
  const files = feedFileAddress(window.location.origin)
  const copyButton = (label: string, value: string) =>
    canCopy() && (
      <Button isIconOnly variant="ghost" size="sm" className="connect-copy" aria-label={`${say('feed.copy')}: ${label}`} onPress={() => copy(value)}>
        <Icon name="copy" size={15} />
      </Button>
    )

  return (
    <>
      <PageHead
        title={say('admin.section.dataFeed')}
        line={say('connect.line.dataFeed')}
        actions={
          !live ? (
            <Button variant="primary" isDisabled={busy} onPress={() => void act('enable')}>
              {say('feed.turnOn')}
            </Button>
          ) : (
            <>
              <Button variant="secondary" isDisabled={busy} onPress={() => void act('check')}>
                <Icon name="refresh" size={14} /> {say('feed.check')}
              </Button>
              <Confirm
                ask={say('feed.turnOff')}
                question={say('feed.turnOffAsk')}
                yes={say('feed.turnOff')}
                keep={say('feed.keep')}
                busy={busy}
                onYes={() => void act('disable')}
              />
            </>
          )
        }
      />
      {toast}
      {error && (
        <Notice className="connect-gap">{error}</Notice>
      )}

      {secret && (
        <div className="connect-gap">
          <OneTimeSecret
            role="alertdialog"
            title={say(secret.kind === 'password' ? 'feed.password' : 'feed.key')}
            note={say('feed.shownOnce')}
            value={secret.value}
            labels={{ show: say('connect.show'), hide: say('connect.hide'), copy: say('feed.copy'), done: say('feed.done') }}
            onCopied={() => flash(say('common.copied'))}
            onDone={() => setSecret(null)}
          />
        </div>
      )}

      <SettingsCard
        title={say('connect.feed.login')}
        status={<span className="connect-mono">{feed.login}</span>}
        label={say('admin.section.dataFeed')}
        aside={<StatusChip tone={STATE_TONE[state]}>{say(STATE_KEY[state])}</StatusChip>}
      >
        {feed.hostLocal && (
          <div className="connect-cardnote">
            <p className="connect-callout" role="note">
              <Icon name="alert" size={15} /> {say('feed.hostLocal', { host: feed.host || '—' })}
            </p>
          </div>
        )}
        <SettingRow label={say('connect.feed.connection')} description={say('connect.feed.connectionLine')}>
          <Values
            items={[
              ...facts.map(([label, value]) => ({
                key: label,
                label: say(label),
                value: (
                  <span className="connect-copyline">
                    <span className="connect-mono">{value}</span>
                    {copyButton(say(label), value)}
                  </span>
                ),
              })),
              { key: 'ssl', label: say('feed.ssl'), value: <span className="connect-mono">{FEED_SSLMODE}</span> },
            ]}
          />
          {canCopy() && (
            <Button variant="secondary" size="sm" onPress={() => copy(connectionText(feed))}>
              <Icon name="copy" size={14} /> {say('feed.copyAll')}
            </Button>
          )}
        </SettingRow>
        {live && (
          <SettingRow control label={say('feed.newPassword')} description={say('connect.feed.passwordLine')}>
            <Confirm
              ask={say('feed.newPassword')}
              question={say('feed.newPasswordAsk')}
              yes={say('feed.newPassword')}
              keep={say('feed.keep')}
              busy={busy}
              onYes={() => void act('password')}
            />
          </SettingRow>
        )}

        {check && (
          <SettingBlock>
            {check.problems.length === 0 ? (
              <TestResult ok title={say('connect.passed')}>
                <span>{say('feed.checkOk')}</span>
              </TestResult>
            ) : (
              <TestResult ok={false} title={say('connect.needsWork')}>
                <ul className="connect-problems">
                  {check.problems.map((code) => {
                    const key = problemKey(code, feed.serverMakesLogins)
                    return <li key={code}>{key ? say(key) : <span className="connect-mono">{code}</span>}</li>
                  })}
                </ul>
              </TestResult>
            )}
          </SettingBlock>
        )}
      </SettingsCard>

      {script && (
        <SettingsCard
          title={say('feed.script')}
          aside={
            canCopy() && (
              <Button variant="secondary" size="sm" onPress={() => copy(script)}>
                <Icon name="copy" size={14} /> {say('feed.copy')}
              </Button>
            )
          }
        >
          <div className="connect-cardbody">
            <pre className="connect-script">{script}</pre>
          </div>
        </SettingsCard>
      )}

      {live && (
        <SettingsCard
          title={say('feed.key')}
          aside={
            feed.keySet ? (
              <>
                <Masked />
                <Confirm
                  ask={say('feed.newKey')}
                  question={say('feed.newKeyAsk')}
                  yes={say('feed.newKey')}
                  keep={say('feed.keep')}
                  busy={busy}
                  onYes={() => void act('key')}
                />
              </>
            ) : (
              <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => void act('key')}>
                <Icon name="plus" size={14} /> {say('feed.newKey')}
              </Button>
            )
          }
        >
          <SettingRow label={say('feed.files')} description={say('connect.feed.filesLine')}>
            <Values
              items={[
                {
                  key: 'files',
                  label: say('connect.feed.address'),
                  value: (
                    <span className="connect-copyline">
                      <span className="connect-mono">{files}</span>
                      {copyButton(say('feed.files'), files)}
                    </span>
                  ),
                },
                {
                  key: 'made',
                  label: say('feed.keyMade'),
                  value: feed.keySet && feed.keyCreatedAt ? formatMoment(feed.keyCreatedAt, lang, { date: true, time: true }) : '—',
                },
              ]}
            />
          </SettingRow>
        </SettingsCard>
      )}
    </>
  )
}
